import { create } from 'zustand';
import { Domain } from '../model/types';
import { useWorkplaceStore } from '@host/stores/workplace.store';
import { GlobalAudioNotificationService } from '@host/services/audio-notification.service';
import { useAdministrationStore } from '@host/stores/administration.store';
import { useMultiFloorStateStore } from './MultiFloorStore';
import { PolicyTypeEnum } from '@/shared/enums/policyType.enum';
import { Logger } from '@/shared/services/logger.service';
import { peekPendingRoomRestore } from '@host/utils/pending-room-restore.util';
import { trackRoomLockToggled } from '@host/utils/office-tracking';
// Define the store type
interface MyFloorStore {
  showFloor: boolean;
  selectedFloors: Array<Domain>;
  showHideFloor: () => void;
  addFloor: (floor: Domain) => void;
  removeFloor: (floorId: number) => void;
  officeMap: Array<Domain>;
  setOfficeMap: (floor: Domain) => void;
  currentFloorId: string | null;
  setCurrentFloorId: (pid: string) => void;

  // Set while usePresenceLifecycle is restoring the floor/room a user was on
  // before a refresh. CarouselView's own default-floor-selection effects race
  // that async restore on a fixed timer regardless of network speed — this
  // flag lets them defer to the restore instead of fighting over
  // currentFloorId.
  isRestoringFloor: boolean;
  setIsRestoringFloor: (val: boolean) => void;

  officeView: 'carousel' | 'grid';
  handleOfficeChangeView: (view: 'carousel' | 'grid') => void;
  updateSelectedFloors: (floor: Domain) => void;

  currentRoomLocked: boolean;
  /** `source` defaults to 'user'; 'call' marks a lock applied because the agent is on a call. */
  setCurrentRoomLocked: (source?: 'user' | 'call') => void;
  showLockNotification: boolean;
  setShowLockNotification: (show: boolean) => void;
  syncLockStatusFromWorkplace: (userPid?: string, userStatus?: string) => void;

  /** True while the current lock was applied automatically because the agent is on a call. */
  callAutoLocked: boolean;
  /** True when the agent manually unlocked during a call — suppresses re-locking until it ends. */
  callLockOverridden: boolean;
  /** Drive the room lock from the current user's own dialer status. */
  applyCallLock: (isOnCall: boolean) => void;
}

// A refresh restore starts on the saved floor with the default-floor pick held off.
const pendingRestore = peekPendingRoomRestore();
// Failsafe so a restore that never runs can't hold the default-floor pick off forever.
const RESTORE_FAILSAFE_MS = 20_000;

// Create Zustand store
export const useMyFloorStore = create<MyFloorStore>((set) => ({
  showFloor: false,
  selectedFloors: [],
  officeMap: [],
  currentFloorId: pendingRestore?.floorPid ?? null,
  isRestoringFloor: !!pendingRestore,
  setIsRestoringFloor: (val: boolean) => set(() => ({ isRestoringFloor: val })),
  officeView: 'carousel',
  currentRoomLocked: false,
  showLockNotification: false,
  callAutoLocked: false,
  callLockOverridden: false,

  // Toggles the showFloor state
  showHideFloor: () => set((state) => ({ showFloor: !state.showFloor })),

  // Adds a new floor to selectedFloors
  addFloor: (floor) =>
    set((state) => {
      const result = state.selectedFloors.find((e) => e.DomainId == floor.DomainId);
      if (result) return state;
      return {
        selectedFloors: [floor, ...state.selectedFloors],
      };
    }),

  // Removes a floor from selectedFloors by its floorId
  removeFloor: (floorId) =>
    set((state) => ({
      selectedFloors: state.selectedFloors.filter((f) => f.FloorplanId !== floorId),
    })),

  updateSelectedFloors: (floor: Domain) =>
    set((state) => {
      const filteredFloors =
        state.selectedFloors.length > 0
          ? state.selectedFloors.filter((f) => f.FloorplanId !== floor.FloorplanId)
          : [];
      const updatedFloors: Domain[] = [floor, ...filteredFloors];
      return { selectedFloors: updatedFloors };
    }),

  setOfficeMap: (floor) => set(() => ({ officeMap: [floor] })),

  setCurrentFloorId: (floorPid: string) => set(() => ({ currentFloorId: floorPid })),

  handleOfficeChangeView: (view: 'carousel' | 'grid') => set(() => ({ officeView: view })),
  setCurrentRoomLocked: (source: 'user' | 'call' = 'user') =>
    set((state) => {
      const workplaceStore = useWorkplaceStore.getState();
      const canLock = workplaceStore.canLockCurrentRoom();

      if (!canLock) {
        return state;
      }

      // The policy only blocks locking — unlocking must always be permitted so a
      // user whose policy is activated while their room is already locked can still
      // get out of the locked state.
      const tryingToLock = !state.currentRoomLocked;
      if (tryingToLock) {
        const { policies, policiesLoadedInSession } = useAdministrationStore.getState();
        if (policiesLoadedInSession) {
          const floorPid = useMultiFloorStateStore.getState().currentFloorPresence;
          const userPid = workplaceStore.user?.pid;
          const key = PolicyTypeEnum.LOCKED_OFFICE;

          const floorPolicy = floorPid
            ? policies.find(
                (p) => p.policy.key === key && p.scope === 'floor' && p.scope_pid === floorPid
              )
            : undefined;
          const orgPolicy = policies.find(
            (p) => p.policy.key === key && p.scope === 'organization'
          );
          const effective = floorPolicy ?? orgPolicy;

          const isPolicyBlocking =
            !!effective?.active && !(userPid && effective.exceptions?.users?.includes(userPid));

          if (isPolicyBlocking) {
            return state;
          }
        }
      }

      const newLockedState = !state.currentRoomLocked;

      // Play lock/unlock sound
      if (newLockedState) {
        GlobalAudioNotificationService.play('lock');
      } else {
        GlobalAudioNotificationService.play('unlock');
      }

      // Update workplace store with locked status
      workplaceStore.setRoomLocked(newLockedState);

      // A manual unlock while the call lock is in force is the agent overriding it. Remember
      // that so the next status poll doesn't immediately lock them back in.
      const overriddenByUser = source === 'user' && !newLockedState && state.callAutoLocked;
      if (source === 'user') trackRoomLockToggled(newLockedState, overriddenByUser);

      return {
        currentRoomLocked: newLockedState,
        showLockNotification: true,
        callAutoLocked: source === 'call' ? newLockedState : false,
        callLockOverridden: overriddenByUser ? true : state.callLockOverridden,
      };
    }),

  /**
   * Lock and unlock the agent's own room as their call starts and ends.
   *
   * This goes through the ordinary lock path rather than deriving a lock elsewhere, so the
   * toolbar button, the room tint, entry blocking and the presence event all stay in step —
   * the agent sees "Unlock Room" while on a call, and unlocking genuinely works.
   *
   * @param isOnCall - Whether the current user is on a live contact in the dialer
   */
  applyCallLock: (isOnCall: boolean) =>
    set((state) => {
      if (!useWorkplaceStore.getState().canLockCurrentRoom()) {
        return state;
      }

      if (isOnCall) {
        // The agent chose to unlock during this call — respect it until the call ends.
        if (state.callLockOverridden || state.currentRoomLocked) {
          return state;
        }
        queueMicrotask(() => useMyFloorStore.getState().setCurrentRoomLocked('call'));
        return state;
      }

      // Call over. Only undo a lock we applied — a room the agent locked themselves stays shut.
      if (state.callAutoLocked) {
        queueMicrotask(() => useMyFloorStore.getState().setCurrentRoomLocked('call'));
      }

      return state.callLockOverridden || state.callAutoLocked
        ? { callLockOverridden: false, callAutoLocked: false }
        : state;
    }),

  setShowLockNotification: (show: boolean) => set(() => ({ showLockNotification: show })),

  syncLockStatusFromWorkplace: (userPid?: string, userStatus?: string) =>
    set((state) => {
      const workplaceStore = useWorkplaceStore.getState();
      const currentUserPid = workplaceStore.getUserContext().user_pid;

      // If userPid is provided, only sync if it's for the current user
      if (userPid && userPid !== currentUserPid) {
        return state; // Don't sync lock status for other users
      }

      // Use provided userStatus or fall back to current user's status
      const statusToCheck = userStatus || workplaceStore.getUserStatus();
      const canLock = workplaceStore.canLockCurrentRoom();

      // Only sync if user can lock the current room and status is 'locked'
      const shouldBeLocked = canLock && statusToCheck === 'locked';

      // Only update if the state is different to avoid unnecessary re-renders
      if (state.currentRoomLocked !== shouldBeLocked) {
        Logger.debug(
          `[MyFloorStore] Syncing lock status: ${shouldBeLocked} (user status: ${statusToCheck}, userPid: ${userPid || 'current'})`
        );
        return { currentRoomLocked: shouldBeLocked };
      }

      return state;
    }),
}));

if (pendingRestore) {
  setTimeout(() => useMyFloorStore.getState().setIsRestoringFloor(false), RESTORE_FAILSAFE_MS);
}
